/**
 * Depot checker - modal, storage and shared API.
 *
 * The modal is opened on demand only - it never opens by itself. Triggers are
 * any element matching OPENER_SELECTOR, or loading a page with the
 * #postcode_checker hash.
 *
 * Storage model:
 *   localStorage.deliverydepot === null        never checked      -> ordering blocked
 *   localStorage.deliverydepot === ''          checked, no cover  -> ordering blocked
 *   localStorage.deliverydepot === 'Gamrie'    valid depot
 *
 * Public API (window.DepotChecker):
 *   .ready              Promise, resolves once the postcode data has loaded
 *   .get()              'Gamrie' | '' | null
 *   .state()            'inside' | 'outside' | 'unset'
 *   .set(depot)         store a depot and notify listeners
 *   .clear()            forget the depot
 *   .depotNames()       ['Invergordon', 'Gamrie', ...] from the data file
 *   .lookup(postcode)   { status, depot, message }
 *   .open() / .close()
 *   .onChange(fn)       fn(depot) whenever the stored depot changes
 */
(function () {
  'use strict';

  var KEY_DEPOT = 'deliverydepot';

  // Anything matching this opens the checker. The theme's existing
  // "Postcode Delivery Checker" link uses the href, so it works untouched.
  var OPENER_SELECTOR = '[data-depot-checker-open], a[href="#postcode_checker"]';

  var lookup = null;
  var listeners = [];
  var resolveReady;
  var ready = new Promise(function (resolve) { resolveReady = resolve; });

  /* ---------------------------------------------------------------- storage */

  function read(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function write(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  function remove(key) {
    try { localStorage.removeItem(key); } catch (e) { /* private mode */ }
  }

  function getDepot() {
    return read(KEY_DEPOT);
  }

  function state() {
    var depot = getDepot();
    if (depot === null) return 'unset';
    if (depot === '') return 'outside';
    return 'inside';
  }

  function notify() {
    var depot = getDepot();
    listeners.forEach(function (fn) {
      try { fn(depot); } catch (e) { console.error('[depot-checker] listener failed', e); }
    });
    document.dispatchEvent(new CustomEvent('depot:change', { detail: { depot: depot } }));
  }

  function setDepot(depot) {
    write(KEY_DEPOT, depot === null || depot === undefined ? '' : String(depot));
    notify();
  }

  function clearDepot() {
    remove(KEY_DEPOT);
    notify();
  }

  /* ------------------------------------------------------------------ modal */

  function root() {
    return document.getElementById('depot-checker');
  }

  function showPanel(name) {
    var el = root();
    if (!el) return;
    var panels = el.querySelectorAll('[data-dc-panel]');
    for (var i = 0; i < panels.length; i++) {
      panels[i].hidden = panels[i].getAttribute('data-dc-panel') !== name;
    }
  }

  function setDepotText(depot) {
    var el = root();
    if (!el) return;
    var slots = el.querySelectorAll('[data-dc-depot]');
    for (var i = 0; i < slots.length; i++) slots[i].textContent = depot || '';
  }

  function open() {
    var el = root();
    if (!el) return;
    el.hidden = false;
    document.documentElement.classList.add('dc-modal-open');
    showPanel('form');
    var input = document.getElementById('depot-checker-input');
    if (input) {
      input.value = '';
      // Defer so the element is actually visible before we focus it.
      setTimeout(function () { input.focus(); }, 0);
    }
  }

  function close() {
    var el = root();
    if (!el) return;
    el.hidden = true;
    document.documentElement.classList.remove('dc-modal-open');
  }

  function submit() {
    var input = document.getElementById('depot-checker-input');
    if (!input || !lookup) return;

    var result = lookup.lookup(input.value);

    if (result.status === 'invalid') {
      showPanel('invalid');
      return;
    }

    if (result.status === 'restricted') {
      var messageEl = root() && root().querySelector('[data-dc-restricted-message]');
      if (messageEl && result.message) messageEl.innerHTML = result.message;
      setDepot(result.depot);
      showPanel('restricted');
      return;
    }

    if (result.status === 'none') {
      setDepot('');            // empty string = checked, not covered
      showPanel('sorry');
      return;
    }

    setDepotText(result.depot);
    setDepot(result.depot);
    showPanel('yes');
  }

  function bindModal() {
    var el = root();
    if (!el) return;

    var form = document.getElementById('depot-checker-form');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        submit();
      });
    }

    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-dc-close], [data-dc-continue]')) { e.preventDefault(); close(); return; }
      if (e.target.closest('[data-dc-retry]')) { e.preventDefault(); open(); return; }
      // Click on the dimmed backdrop, not the dialog itself.
      if (e.target.hasAttribute('data-dc-backdrop')) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !el.hidden) close();
    });

    // Any opener, anywhere on the page - including links the theme renders
    // before this script runs, since the listener is delegated.
    //
    // Capture phase + stopPropagation, because the theme's own link carries
    // class="overlay-tertiary" and may already be wired to a built-in overlay
    // targeting #postcode_checker. This claims the click before that runs, so
    // the two can't both open.
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;

      if (target.closest(OPENER_SELECTOR)) {
        e.preventDefault();
        e.stopPropagation();
        open();
      }
    }, true);
  }

  /* ------------------------------------------------------------------- init */

  function loadData() {
    var url = window.DEPOT_POSTCODES_URL;
    if (!url) {
      console.error('[depot-checker] window.DEPOT_POSTCODES_URL is not set - render the depot-checker snippet.');
      resolveReady(null);
      return;
    }

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        lookup = new window.PostcodeLookup(data);
        resolveReady(lookup);
      })
      .catch(function (err) {
        // Fail OPEN: if coverage data is unavailable we must not block the
        // whole shop. Ordering rules are skipped until it loads.
        console.error('[depot-checker] could not load postcode data - depot rules disabled', err);
        resolveReady(null);
      });
  }

  function start() {
    bindModal();

    // The Check button can't do anything until the coverage data is here.
    var submitButton = document.querySelector('#depot-checker-form [type="submit"]');
    if (submitButton) submitButton.disabled = true;

    loadData();

    ready.then(function () {
      if (submitButton) submitButton.disabled = false;

      // Deep link: /pages/whatever#postcode_checker opens the checker directly,
      // so the theme's anchor still works from another page.
      if (window.location.hash === '#postcode_checker') open();
    });
  }

  /* -------------------------------------------------------------------- API */

  window.DepotChecker = {
    ready: ready,
    get: getDepot,
    state: state,
    set: setDepot,
    clear: function () { clearDepot(); },
    depotNames: function () { return lookup ? lookup.depotNames() : []; },
    lookup: function (postcode) { return lookup ? lookup.lookup(postcode) : null; },
    isLoaded: function () { return lookup !== null; },
    open: open,
    close: close,
    onChange: function (fn) { listeners.push(fn); }
  };

  // Keep tabs in sync if the customer has two windows open.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY_DEPOT) notify();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
